import React, { useRef, useState } from "react";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import { closeView, viewPositionSelector } from "~redux/slices/edstSlice";
import { useDragging } from "hooks/useDragging";
import { baseBorderColor, colorNameMap, useFocused, usePixiOnClickOutside } from "@poscon/shared-frontend";
import { InteractiveContainer } from "~/components/utils/InteractiveContainer";
import { layerZIndexMap } from "~/layerZIndexMap";
import { cyan, defaultTint, edstFontDim } from "~/utils/constants";
import { MenuTitleBar } from "~/components/utils/MenuTitleBar";
import { TableField } from "../utils/TableField";
import {
  gpdEnabledGeomapsSelector,
  gpdGeomapConfigSelector,
  setGpdGeomapEnabled,
} from "~/redux/slices/gpdSlice";
import { RadioButton } from "../utils/RadioButton";
import { EdstButton, exitButtonHeight } from "~/components/utils/EdstButton";

const width = edstFontDim.width * 24;

type Submenu = "mapFeatures" | "aircraftDisplay";

export const GpdMapOptions = () => {
  const dispatch = useRootDispatch();
  const pos = useRootSelector((state) => viewPositionSelector(state, "GPD_MAP_OPTIONS_MENU"));

  const [submenu, setSubmenu] = useState<Submenu | null>(null);
  const ref = useRef(null);
  const focused = useFocused(ref);

  const { startDrag } = useDragging(ref, "GPD_MAP_OPTIONS_MENU", "mouseup");

  usePixiOnClickOutside(ref, () => dispatch(closeView("GPD_MAP_OPTIONS_MENU")));

  return (
    <InteractiveContainer ref={ref} x={pos.x} y={pos.y} zIndex={layerZIndexMap.prompts} sortableChildren>
      <MenuTitleBar
        title="Map Options"
        width={width}
        onTitleMouseDown={startDrag}
        view="GPD_MAP_OPTIONS_MENU"
        fill={focused ? cyan : colorNameMap.mediumGrey}
        tint={colorNameMap.black}
        withX={false}
      />
      <container y={edstFontDim.height + 3}>
        {submenu === null && <MenuSelection setOpenSubmenu={setSubmenu} />}
        {submenu === "mapFeatures" && <MapFeaturesMenu />}
        {submenu === "aircraftDisplay" && <AircraftDisplayOptions />}
      </container>
    </InteractiveContainer>
  );
};

const MenuSelection = ({ setOpenSubmenu }: { setOpenSubmenu: (menu: Submenu) => void }) => {
  const dispatch = useRootDispatch();
  const height = (edstFontDim.height + 4) * 2 + 8 + exitButtonHeight;

  return (
    <>
      <graphics
        draw={(graphics) => {
          graphics.clear();
          graphics.rect(0, 0, width, height).fill(0).stroke({ width: 1, color: baseBorderColor });
        }}
      />
      <TableField
        textX={2}
        x={4}
        y={4}
        width={width - 8}
        text="Map Features..."
        tint={defaultTint}
        onmousedown={() => {
          setOpenSubmenu("mapFeatures");
        }}
      />
      <TableField
        textX={2}
        x={4}
        y={edstFontDim.height + 10}
        width={width - 8}
        text="AC Display Menu..."
        tint={defaultTint}
        onmousedown={() => {
          setOpenSubmenu("aircraftDisplay");
        }}
      />
      <EdstButton
        x={width - edstFontDim.width * 4 - 15}
        y={height - (edstFontDim.height + 8)}
        text="Exit"
        onmousedown={() => dispatch(closeView("GPD_MAP_OPTIONS_MENU"))}
      />
    </>
  );
};

const MapFeaturesMenu = () => {
  const dispatch = useRootDispatch();
  const geomapConfig = useRootSelector(gpdGeomapConfigSelector);
  const enabledGeomaps = useRootSelector(gpdEnabledGeomapsSelector);

  const geomaps = Object.entries(geomapConfig?.geomaps ?? {});

  const height = (edstFontDim.height + 4) * geomaps.length + 6 + exitButtonHeight;

  return (
    <>
      <graphics
        draw={(graphics) => {
          graphics.clear();
          graphics.rect(0, 0, width, height).fill(0).stroke({ width: 1, color: baseBorderColor });
        }}
      />
      {geomaps.map(([id, geomap], index) => {
        const isEnabled = enabledGeomaps.includes(id);
        return (
          <container key={id} y={4 + index * (edstFontDim.height + 4)} eventMode="static">
            <RadioButton
              x={4 + Math.floor(edstFontDim.width / 2 + 2)}
              y={1 + Math.floor(edstFontDim.height / 2)}
              size={10}
              borderSize={2}
              selected={isEnabled}
              variant="square"
              disabled
            />
            <TableField
              key={id}
              textX={2}
              x={24}
              width={width - 28}
              text={geomap.label}
              tint={defaultTint}
              onmousedown={() => {
                dispatch(setGpdGeomapEnabled({ id, enabled: !isEnabled }));
              }}
            />
          </container>
        );
      })}
      <EdstButton
        x={width - edstFontDim.width * 4 - 15}
        y={height - (edstFontDim.height + 8)}
        text="Exit"
        onmousedown={() => dispatch(closeView("GPD_MAP_OPTIONS_MENU"))}
      />
    </>
  );
};

const AircraftDisplayOptions = () => {
  const dispatch = useRootDispatch();

  const height = exitButtonHeight + 4;

  return (
    <>
      <graphics
        draw={(graphics) => {
          graphics.clear();
          graphics.rect(0, 0, width, height).fill(0).stroke({ width: 1, color: baseBorderColor });
        }}
      />
      <EdstButton
        x={width - edstFontDim.width * 4 - 15}
        y={height - (edstFontDim.height + 8)}
        text="Exit"
        onmousedown={() => dispatch(closeView("GPD_MAP_OPTIONS_MENU"))}
      />
    </>
  );
};
